import { useAppDispatch, useAppSelector } from "../../../hooks";
import { useCallback, useState } from "react";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { CustomToast } from "../CustomToast";
import {
  campaignsApi,
  useGeneratePlaybookForMembersMutation,
  useStartAccountPlanGenerationMutation,
} from "../../../api";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { setIsNotificationsOpen } from "../../../features/companySlice";
import { selectPermissions } from "../../../features/permissionsSlice";
import {
  ButtonsColorType,
  ButtonsType,
  PlaybookPlanNames,
} from "../../../types/enums";
import { ReuseButton } from "../ReuseButton";

export const GetAccountPlanBtn = ({
  id,
  fullWidth,
  campaign_id,
  isPlaybook,
}: {
  id: string;
  campaign_id: string;
  fullWidth?: boolean;
  isPlaybook: boolean;
}) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { isAllowedEditGenerateReports } = useAppSelector(selectPermissions);

  const [startGeneration, { isLoading: isGenerating }] =
    useStartAccountPlanGenerationMutation();

  const [generatePlaybookForMembers, { isLoading: isGeneratingPlaybooks }] =
    useGeneratePlaybookForMembersMutation();

  const handleStartGeneration = useCallback(async () => {
    setIsLoading(true);
    await (
      isPlaybook
        ? generatePlaybookForMembers({
            campaign_id,
            ids: [id],
            include_all: false,
          })
        : startGeneration({
            account_id: id,
            campaign_id,
          })
    )
      .unwrap()
      .catch((err) => {
        setIsLoading(false);
        toast.error(
          <CustomToast
            type="error"
            title="Failed to Start"
            text={
              getErrorMessage(err).globalError || "Failed to start generation"
            }
          />,
        );
      });

    dispatch(campaignsApi.util.invalidateTags(["activities"]));
    dispatch(setIsNotificationsOpen(true));
  }, [id, startGeneration, dispatch]);

  return (
    <ReuseButton
      fullWidth={fullWidth}
      onClick={handleStartGeneration}
      buttonColor={ButtonsColorType.gradient}
      buttonType={ButtonsType.contained}
      disabled={
        isLoading ||
        isGenerating ||
        isGeneratingPlaybooks ||
        !isAllowedEditGenerateReports
      }
      startIcon={
        (isLoading || isGenerating) && (
          <CircularProgress size={24} sx={{ color: "inherit" }} />
        )
      }
    >
      {isPlaybook
        ? `Generate ${PlaybookPlanNames.playbook}`
        : `Get Target ${PlaybookPlanNames.account_plan}`}
    </ReuseButton>
  );
};
